import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import LogoMark from '@/assets/icons/img-logo.svg?react'
import PlateLogo from '@/assets/welcome/plate-logo-horizontal-dark.svg?react'
import IconDocs from '@/assets/icons/icon-docs.svg?react'
import IconArrowDown from '@/assets/icons/icon-arrow-down.svg?react'
import IconCheck from '@/assets/icons/icon-check.svg?react'
import IconCopy from '@/assets/icons/icon-copy.svg?react'
import SlackIcon from '@/assets/icons/icon-slack.svg?react'
import GithubIcon from '@/assets/icons/icon-github.svg?react'
import FigmaIcon from '@/assets/icons/icon-figma.svg?react'
import OpenAIIcon from '@/assets/icons/icon-openai.svg?react'
import ClaudeIcon from '@/assets/icons/icon-claude-mark.svg?react'
import VideoIntroPoster from '@/assets/welcome/intro-poster.jpg'
import css from './WelcomeV2.module.css'

const CHANGELOG_ITEMS: { title: string; body: ReactNode | null }[] = [
    {
        title: 'Global search',
        body: 'Find any task or project in a keystroke — open search from the sidebar or with ⌘K, type a name or number, and jump straight there with the arrow keys.',
    },
    {
        title: 'Slack Integration',
        body: <>Get task updates in a channel, create tasks from any message (with its image), and ask the @plate agent right in the thread. <a href="/docs/apps/slack" target="_blank" rel="noopener noreferrer" className={css.changelog_docs_link}>Guide</a></>,
    },
    {
        title: 'Plate is now in ChatGPT',
        body: <>Plate is an official ChatGPT app. Add it once and manage your projects and tasks right inside ChatGPT — just by chatting. <a href="https://chatgpt.com/apps/plate/asdk_app_6a25adb4ed6881919eb1f9f08ce9e53f" target="_blank" rel="noopener noreferrer" className={css.changelog_docs_link}>Open in ChatGPT</a> · <a href="/docs/apps/chatgpt" target="_blank" rel="noopener noreferrer" className={css.changelog_docs_link}>Guide</a></>,
    },
    {
        title: 'Email notifications',
        body: <>Plate now sends email summaries for activity you haven't seen yet — so you never miss an update when you're away from the app. <a href="/docs/notifications" target="_blank" rel="noopener noreferrer" className={css.changelog_docs_link}>Learn more.</a></>,
    },
    {
        title: 'Performance',
        body: 'We spent serious time on load performance. Smarter data fetching, parallel loading, and better caching — workspaces now open 5× faster.',
    },
]

const FAQ_ITEMS = [
    {
        question: 'What is Plate?',
        answer: 'Plate is a task manager where AI agents work as part of your team. Add an agent to a project like a teammate: hand it a task or ask it a question in the comments, and it answers with real work — drafts, summaries, answers — right where your team already is.',
    },
    {
        question: 'How is Plate different from other tools?',
        answer: 'Most task managers were built for people only. In Plate, an AI agent is a member of the project — you assign it tasks and @mention it in comments, and it works alongside your team instead of in a separate chat tab. And the tool itself stays radically simple: workspaces, projects, tasks. No configuration.',
    },
    {
        question: 'What can the agent actually do?',
        answer: 'Anything that needs the project as context: groom the backlog and flag duplicates, write release notes from the tasks you actually closed, summarize a long thread, answer "have we done this before?" — and post it all where the team already works.',
    },
    {
        question: 'Can I use Plate without AI?',
        answer: "Of course. Plate is a clean, fast task manager on its own. The agent is there when you want it, invisible when you don't.",
    },
    {
        question: 'Can I use Plate solo or is it only for teams?',
        answer: 'Both. It works just as well for personal project planning as it does for small-mid teams.',
    },
    {
        question: 'Do I need to install anything?',
        answer: 'No. Plate runs in your browser, so you can start using it right away without installing anything.',
    },
    {
        question: 'Can I use Plate for free?',
        answer: 'Yes. Plate offers a free plan with some limits on projects, tasks, and features.',
    },
    {
        question: 'What happens if I cancel my subscription?',
        answer: 'Your plan will remain active until the end of the current billing period. After that, your workspace will continue on the free plan.',
    },
    {
        question: 'Do you offer refunds?',
        answer: <>Yes. If our service didn't meet your expectations, contact us at <a href="mailto:hello@plate.to">hello@plate.to</a>. Refunds are available within 14 days of the first payment.</>,
    },
]

// Experimental v2 landing (Figma "Plate Apr 26"). Sticky header + a 744px content
// column of sections.
const WelcomeV2 = () => {
    const navigate = useNavigate()
    const goAuth = () => navigate('/auth')

    const [openChangelog, setOpenChangelog] = useState<number | null>(0)
    const [openFaq, setOpenFaq] = useState<number | null>(null)
    const toggleChangelog = (i: number) => setOpenChangelog(prev => (prev === i ? null : i))
    const toggleFaq = (i: number) => setOpenFaq(prev => (prev === i ? null : i))

    const [copiedMcp, setCopiedMcp] = useState(false)
    const handleCopyMcp = () => {
        navigator.clipboard.writeText('npx mcp-remote https://plate.to/mcp')
        setCopiedMcp(true)
        setTimeout(() => setCopiedMcp(false), 2000)
    }

    // The header logo only appears once the in-content "Plate" wordmark has
    // scrolled out of view (so the two logos never show at once).
    const wordmarkRef = useRef<HTMLDivElement>(null)
    const [showHeaderLogo, setShowHeaderLogo] = useState(false)

    // True while the black integrations band sits under the header — the logo
    // flips to white so it stays visible against the dark background.
    const darkBandRef = useRef<HTMLElement>(null)
    const [headerOnDark, setHeaderOnDark] = useState(false)

    useEffect(() => {
        const el = wordmarkRef.current
        if (!el) return
        const observer = new IntersectionObserver(
            ([entry]) => setShowHeaderLogo(!entry.isIntersecting),
            { threshold: 0 }
        )
        observer.observe(el)
        return () => observer.disconnect()
    }, [])

    useEffect(() => {
        const el = darkBandRef.current
        if (!el) return
        // The logo is over the band while the header's vertical centre (~28px from
        // top) falls inside the band. A scroll handler with getBoundingClientRect is
        // used instead of an IntersectionObserver with a negative rootMargin, which
        // Safari handles unreliably (the collapsed observation area can stop firing).
        const update = () => {
            const rect = el.getBoundingClientRect()
            setHeaderOnDark(rect.top <= 28 && rect.bottom >= 28)
        }
        update()
        window.addEventListener('scroll', update, { passive: true })
        window.addEventListener('resize', update)
        return () => {
            window.removeEventListener('scroll', update)
            window.removeEventListener('resize', update)
        }
    }, [])

    const headerClass = [css.header, showHeaderLogo ? css.header_solid : ''].join(' ')
    const headerLogoClass = [
        css.header_logo,
        showHeaderLogo ? css.header_logo_visible : '',
        headerOnDark ? css.header_logo_white : ''
    ].join(' ')

    return (
        <div className={css.page}>
            <header className={headerClass}>
                <a href="/" className={headerLogoClass} aria-label="Plate">
                    <LogoMark />
                </a>
                <nav className={css.nav}>
                    <a href="/docs" className={css.nav_link}>
                        <IconDocs className={css.nav_icon} />
                        Docs
                    </a>
                    <button type="button" className={css.pill_light} onClick={goAuth} data-testid="welcome-login-btn">Log in</button>
                    <button type="button" className={css.pill_dark} onClick={goAuth}>Sign up</button>
                </nav>
            </header>

            <main className={css.inner}>
                <div className={css.wordmark} ref={wordmarkRef}>
                    <PlateLogo className={css.wordmark_logo} />
                </div>

                <section className={css.hero}>
                    <div className={css.hero_text}>
                        <h1 className={css.hero_title}>
                            People and <span className={css.accent}>AI agents</span> work side by side.
                        </h1>
                        <p className={css.hero_subtitle}>
                            Plate is a task manager where people and AI agents share the same projects.
                            Assign an agent a task, @mention it in a comment, and keep your whole team's
                            work on one clean screen.
                        </p>
                        <div className={css.hero_actions}>
                            <button type="button" className={css.btn_dark} onClick={goAuth}>Get started</button>
                            <a href="/docs/mcp" className={css.btn_outline}>For agents</a>
                        </div>
                    </div>

                    <div className={css.hero_media}>
                        <video
                            className={css.media_video}
                            poster={VideoIntroPoster}
                            autoPlay
                            muted
                            loop
                            playsInline
                            preload="auto"
                        >
                            <source src="/plate-demo-2.mp4" type="video/mp4" />
                        </video>
                    </div>
                </section>

                <section className={css.assistants}>
                    <div className={css.section_intro}>
                        <h2 className={css.section_title}>Work without switching context</h2>
                        <p className={css.section_subtitle}>
                            Use Plate right from your AI assistant — assign tasks, check status,
                            and update projects without leaving the chat.
                        </p>
                    </div>
                    <div className={css.cards2}>
                        <div className={css.int_card}>
                            <div className={css.app_icon}><OpenAIIcon /></div>
                            <h3 className={css.int_card_title}>ChatGPT</h3>
                            <p className={css.int_card_desc}>Plate is an official ChatGPT app — manage your tasks without leaving the conversation.</p>
                            <a
                                href="https://chatgpt.com/apps/plate/asdk_app_6a25adb4ed6881919eb1f9f08ce9e53f"
                                target="_blank"
                                rel="noopener noreferrer"
                                className={css.int_card_link}
                            >
                                Open app →
                            </a>
                        </div>
                        <div className={css.int_card}>
                            <div className={css.app_icon}><ClaudeIcon /></div>
                            <h3 className={css.int_card_title}>Claude</h3>
                            <p className={css.int_card_desc}>Connect Claude over MCP and let it read and update your projects directly.</p>
                            <a href="/docs/mcp" className={css.int_card_link}>MCP docs →</a>
                        </div>
                    </div>
                </section>

                <section className={css.apps}>
                    <div className={css.section_intro}>
                        <h2 className={css.section_title}>Bring your tools into one workspace</h2>
                        <p className={css.section_subtitle}>
                            Connect Slack, GitHub, Figma, and more to pull updates, requests, and
                            designs into Plate — so your team has the context without chasing it
                            across tools.
                        </p>
                    </div>
                    <div className={css.apps_grid}>
                        <a href="/docs/apps/figma" className={css.app_card}>
                            <div className={css.app_card_head}>
                                <span className={css.app_card_logo}><FigmaIcon /></span>
                                <h3 className={css.app_card_title}>Figma</h3>
                            </div>
                            <p className={css.app_card_desc}>Enable embedded previews of your designs in descriptions and comments.</p>
                        </a>
                        <a href="/docs/apps/slack" className={css.app_card}>
                            <div className={css.app_card_head}>
                                <span className={css.app_card_logo}><SlackIcon /></span>
                                <h3 className={css.app_card_title}>Slack</h3>
                            </div>
                            <p className={css.app_card_desc}>Create tasks from any message and get updates where your team already talks.</p>
                        </a>
                        <div className={[css.app_card, css.app_card_inactive].join(' ')}>
                            <div className={css.app_card_head}>
                                <span className={css.app_card_logo}><GithubIcon /></span>
                                <h3 className={css.app_card_title}>GitHub</h3>
                            </div>
                            <p className={css.app_card_desc}>Link pull requests to tasks and keep their status in sync automatically.</p>
                        </div>
                    </div>
                </section>

                <section className={css.integrations} ref={darkBandRef}>
                    <div className={css.band_inner}>
                    <div className={css.section_intro}>
                        <h2 className={css.section_title}>Connect anything, automate everything</h2>
                        <p className={css.section_subtitle}>
                            Connect Plate to your tools, scripts, and AI workflows — through API,
                            MCP, and Webhooks. Use Plate manually, automate it with scripts, or let
                            AI assistants manage routine updates for you.
                        </p>
                    </div>
                    <div className={css.cards3}>
                        <div className={css.int_card}>
                            <div className={css.int_icon}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" /></svg>
                            </div>
                            <h3 className={css.int_card_title}>REST API</h3>
                            <p className={css.int_card_desc}>Create and update projects, sections, and tasks from your own tools.</p>
                            <a href="/docs/api" className={css.int_card_link}>API docs →</a>
                        </div>
                        <div className={css.int_card}>
                            <div className={css.int_icon}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="4" width="16" height="16" rx="2" /><rect x="9" y="9" width="6" height="6" /><line x1="9" y1="2" x2="9" y2="4" /><line x1="15" y1="2" x2="15" y2="4" /><line x1="9" y1="20" x2="9" y2="22" /><line x1="15" y1="20" x2="15" y2="22" /><line x1="2" y1="9" x2="4" y2="9" /><line x1="2" y1="15" x2="4" y2="15" /><line x1="20" y1="9" x2="22" y2="9" /><line x1="20" y1="15" x2="22" y2="15" /></svg>
                            </div>
                            <h3 className={css.int_card_title}>MCP Server</h3>
                            <p className={css.int_card_desc}>Connect AI assistants like Claude, Cursor, and Windsurf to your Plate workspace.</p>
                            <a href="/docs/mcp" className={css.int_card_link}>MCP docs →</a>
                        </div>
                        <div className={css.int_card}>
                            <div className={css.int_icon}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></svg>
                            </div>
                            <h3 className={css.int_card_title}>Webhooks</h3>
                            <p className={css.int_card_desc}>Trigger real-time automations when tasks change.</p>
                            <a href="/docs/webhooks" className={css.int_card_link}>Webhooks docs →</a>
                        </div>
                    </div>
                    <div className={css.mcp}>
                        <code className={css.mcp_code}>
                            <span className={css.mcp_cmd}>npx</span> mcp-remote <span className={css.mcp_url}>https://plate.to/mcp</span>
                        </code>
                        <button
                            type="button"
                            className={css.mcp_copy}
                            onClick={handleCopyMcp}
                            aria-label={copiedMcp ? 'Copied' : 'Copy command'}
                        >
                            {copiedMcp ? <IconCheck /> : <IconCopy />}
                        </button>
                    </div>

                    <div className={css.agent}>
                        <div className={css.section_intro}>
                            <h2 className={css.section_title}>
                                An agent on the team,<br />
                                not a chat in a tab.
                            </h2>
                            <p className={css.section_subtitle}>
                                No AI section to learn. The agent uses the same three moves your team
                                already knows.
                            </p>
                        </div>
                        <div className={css.agent_moves}>
                            <div className={css.agent_move}>
                                <div className={css.agent_move_head}>
                                    <h3 className={css.agent_move_title}>Add it to a project</h3>
                                </div>
                                <p className={css.agent_move_desc}>Like any teammate. It sees that project's tasks, threads, and history — and nothing else. The project is its memory.</p>
                            </div>
                            <div className={css.agent_move}>
                                <div className={css.agent_move_head}>
                                    <h3 className={css.agent_move_title}>Assign it a task</h3>
                                </div>
                                <p className={css.agent_move_desc}>"Write the release notes." It does the work, posts the result for review, and a human ships it.</p>
                            </div>
                            <div className={css.agent_move}>
                                <div className={css.agent_move_head}>
                                    <h3 className={css.agent_move_title}>@mention</h3>
                                </div>
                                <p className={css.agent_move_desc}><span className={css.agent_mention}>@plate have we done this before?</span> It answers right in the thread — because it knows the project.</p>
                            </div>
                        </div>
                        <div className={css.agent_cta}>
                            <button type="button" className={css.btn_light} onClick={goAuth}>Get started free</button>
                        </div>
                        <p className={css.agent_cta_note}>
                            And everything you'd expect: real-time sync, custom statuses, unlimited members on every plan.
                        </p>
                    </div>
                    </div>
                </section>

                <section className={css.changelog}>
                    <div className={css.band_inner}>
                    <h2 className={css.block_title}>Changelog</h2>
                    <div className={css.accordion}>
                        {CHANGELOG_ITEMS.map((item, index) => (
                            <div key={index} className={css.accordion_item}>
                                <button
                                    type="button"
                                    className={css.accordion_head}
                                    onClick={() => item.body && toggleChangelog(index)}
                                >
                                    <span className={css.accordion_title}>{item.title}</span>
                                    <IconArrowDown className={[css.accordion_icon, openChangelog === index ? css.accordion_icon_open : ''].join(' ')} />
                                </button>
                                {openChangelog === index && item.body && (
                                    <div className={css.accordion_body}>{item.body}</div>
                                )}
                            </div>
                        ))}
                    </div>
                    <a href="/docs/changelog/" className={css.changelog_all_link}>View all updates →</a>
                    </div>
                </section>

                <section className={css.faq}>
                    <h2 className={css.block_title}>Quick answers</h2>
                    <div className={css.accordion}>
                        {FAQ_ITEMS.map((item, index) => (
                            <div key={index} className={css.accordion_item}>
                                <button
                                    type="button"
                                    className={css.accordion_head}
                                    onClick={() => toggleFaq(index)}
                                >
                                    <span className={css.accordion_title}>{item.question}</span>
                                    <IconArrowDown className={[css.accordion_icon, openFaq === index ? css.accordion_icon_open : ''].join(' ')} />
                                </button>
                                {openFaq === index && (
                                    <div className={css.accordion_body}>{item.answer}</div>
                                )}
                            </div>
                        ))}
                    </div>
                </section>

                <section className={css.pricing}>
                    <h2 className={css.block_title}>Make it work for you</h2>
                    <div className={css.pricing_cards}>
                        <div className={css.pricing_card}>
                            <span className={css.pricing_label}>Free</span>
                            <span className={css.pricing_price}>$0</span>
                            <span className={css.pricing_desc}>Free for everyone</span>
                            <ul className={css.pricing_features}>
                                <li><IconCheck className={css.pricing_check} />3 projects</li>
                                <li><IconCheck className={css.pricing_check} />Unlimited members</li>
                                <li><IconCheck className={css.pricing_check} />300 tasks</li>
                                <li><IconCheck className={css.pricing_check} />1GB storage</li>
                            </ul>
                            <button type="button" className={css.btn_light} onClick={goAuth}>Get started</button>
                        </div>
                        <div className={css.pricing_card}>
                            <span className={css.pricing_label}>Pro</span>
                            <span className={css.pricing_price}>$8</span>
                            <span className={css.pricing_desc}>Per member / month</span>
                            <ul className={css.pricing_features}>
                                <li><IconCheck className={css.pricing_check} />Unlimited projects</li>
                                <li><IconCheck className={css.pricing_check} />Unlimited members</li>
                                <li><IconCheck className={css.pricing_check} />Unlimited tasks</li>
                                <li><IconCheck className={css.pricing_check} />500GB storage</li>
                            </ul>
                            <button type="button" className={css.btn_dark} onClick={goAuth}>Subscribe</button>
                        </div>
                    </div>
                    <p className={css.pricing_note}>Try it free. No credit card required.</p>
                </section>

                <footer className={css.footer}>
                    <nav className={css.footer_nav}>
                        <span className={css.footer_copy}>© 2026 Plate</span>
                        <a href="/about/" className={css.footer_link}>About</a>
                        <a href="/docs" className={css.footer_link}>Docs</a>
                        <a href="/about/terms" className={css.footer_link}>Terms</a>
                        <a href="/about/privacy" className={css.footer_link}>Privacy</a>
                        <a href="/about/cookies" className={css.footer_link}>Cookies</a>
                    </nav>
                </footer>
            </main>
        </div>
    )
}

export default WelcomeV2
